import { memo, useEffect, useRef } from 'react'
import { calculateChange, toLocaleStringIn } from '../util/general'
import { useSelector } from 'react-redux'
import { selectRecentExpressions, selectRecentExpressionsRefreshTime } from '../selectors/common'
import { RecentExpression } from '../reducer/common.reducer'
import { selectPrimarySecurity } from '../selectors/home'
import { selectUserSecurityExpressions } from '../selectors/security'
import { getExpressionSecurityKey, getSecurityFromCache, getSecurityKey, isFnOSecurity } from '../util/security'
import { convertExpressionToEvaluationExpression, getExpressionFromSaved } from '../util/expression'
import math from '../util/math'
import { WebSocketRequestMessage_Subscription } from '../proto/generated/websocket'
import websocketActions from '../actions/websocket.actions'
import { useDispatch } from 'react-redux'
import { matchPath, useLocation } from 'react-router-dom'
import ROUTES from '../assets/routes'
import store, { RootState } from '../store'
import { Security } from '../proto/generated/security'

const DEFAULT_TITLE = 'tick2trade - Track All Markets'

type PageWrapperPrimaryProps = {
    children: React.ReactNode
}

const setDocumentTitle = (title: string) => {
    if (title !== document.title) {
        document.title = title
    }
}

const getTitleDisplaySymbol = (state: RootState): string => {
    const primaryState = selectPrimarySecurity(state)
    const { displayName } = state.homeStore.primarySecurity

    if (primaryState.expressionMode) {
        const savedExpression = getExpressionFromSaved(selectUserSecurityExpressions(state), primaryState.key)
        if (savedExpression?.name) {
            return savedExpression.name
        }
        return displayName || 'Spread'
    }

    const resolved = primaryState.primary && isFnOSecurity(primaryState.primary) ? primaryState.underlying : primaryState.primary
    if (resolved?.symbol) {
        return resolved.symbol
    }

    const cached = primaryState.key ? getSecurityFromCache(primaryState.key) : undefined
    return displayName || cached?.symbol || cached?.code || 'Security'
}

const resolveTitleSecurity = (state: RootState): { security: Security; snapshotToken: string } | null => {
    const { primarySecurity } = state.homeStore
    const primaryState = selectPrimarySecurity(state)

    if (primaryState.expressionMode || !primarySecurity.key) {
        return null
    }

    let security = primaryState.primary && isFnOSecurity(primaryState.primary) ? primaryState.underlying : primaryState.primary

    if (!security) {
        const cachedPrimary = getSecurityFromCache(primarySecurity.key)
        if (!cachedPrimary) {
            return null
        }
        if (isFnOSecurity(cachedPrimary) && primarySecurity.underlyingSecurityCode) {
            security = getSecurityFromCache(primarySecurity.underlyingSecurityCode) ?? cachedPrimary
        } else {
            security = cachedPrimary
        }
    }

    const snapshotToken = isFnOSecurity(security) && primarySecurity.underlyingSecurityCode
        ? primarySecurity.underlyingSecurityCode
        : getSecurityKey(security.exchange, security.code)

    return { security, snapshotToken }
}

const resolveExpressionSecurities = (state: RootState): Security[] => {
    const primaryState = selectPrimarySecurity(state)
    if (primaryState.expressionSecurities.length) {
        return primaryState.expressionSecurities
    }
    return state.homeStore.primarySecurity.securities
        .map((token) => getSecurityFromCache(token))
        .filter((sec): sec is Security => !!sec)
}

const updateDocumentTitleFromStore = () => {
    const state = store.getState()
    const snapshots = state.securityStore.snapshots
    const primaryState = selectPrimarySecurity(state)
    const displaySymbol = getTitleDisplaySymbol(state)
    const hasPrimaryKey = !!state.homeStore.primarySecurity.key

    if (primaryState.expressionMode) {
        const expressionSecurities = resolveExpressionSecurities(state)
        if (!expressionSecurities.length) {
            if (hasPrimaryKey) {
                setDocumentTitle(displaySymbol)
            } else {
                setDocumentTitle(DEFAULT_TITLE)
            }
            return
        }

        try {
            const {
                expression: evaluationExpression,
                context
            } = convertExpressionToEvaluationExpression(primaryState.key, expressionSecurities)
            const ltp: Record<string, any> = { ...context }
            const change: Record<string, any> = { ...context }
            const previousClose: Record<string, any> = { ...context }

            for (let c = 0; c < expressionSecurities.length; c++) {
                const sec = expressionSecurities[c]
                const token = getSecurityKey(sec.exchange, sec.code)
                const sn = snapshots[token]
                if (!sn || !sec) {
                    setDocumentTitle(displaySymbol)
                    return
                }
                const expressionKey = getExpressionSecurityKey(sec.exchange, sec.code)
                const priceMultiplier = sec.priceMultiplier || 100
                change[expressionKey] = sn.lastTradedPrice / priceMultiplier - sn.previousClose / priceMultiplier
                ltp[expressionKey] = sn.lastTradedPrice / priceMultiplier
                previousClose[expressionKey] = sn.previousClose / priceMultiplier
            }
            const ltpResult: number = math.evaluate(evaluationExpression, ltp)
            const changeResult: number = math.evaluate(evaluationExpression, change)
            const previousCloseResult: number = math.evaluate(evaluationExpression, previousClose)

            const changePercentage = changeResult * 100 / Math.abs(previousCloseResult)
            const positive = changePercentage > 0
            const negative = changePercentage < 0
            let newTitle = `${displaySymbol} ${toLocaleStringIn(ltpResult)}`
            if (ltpResult >= 0) {
                newTitle += ` ${positive ? '▲' : negative ? '▼' : ''} ${positive ? '+' : ''}${positive || negative ? changePercentage.toFixed(2) + '%' : ''}`
            }
            setDocumentTitle(newTitle)
        } catch {
            setDocumentTitle(hasPrimaryKey ? displaySymbol : DEFAULT_TITLE)
        }
        return
    }

    const titleSecurity = resolveTitleSecurity(state)
    if (!titleSecurity) {
        setDocumentTitle(hasPrimaryKey ? displaySymbol : DEFAULT_TITLE)
        return
    }

    const { security, snapshotToken } = titleSecurity
    const mainSecuritySnapshot = snapshots[snapshotToken]
    if (!mainSecuritySnapshot) {
        setDocumentTitle(displaySymbol)
        return
    }

    const change = calculateChange(mainSecuritySnapshot.lastTradedPrice, mainSecuritySnapshot.previousClose)
    const positive = change.changePercentage > 0
    const negative = change.changePercentage < 0
    const newTitle = `${displaySymbol} ${toLocaleStringIn(mainSecuritySnapshot.lastTradedPrice / (security.priceMultiplier ?? 100))} ${positive ? '▲' : negative ? '▼' : ''} ${positive ? '+' : ''}${positive || negative ? change.changePercentage.toFixed(2) + '%' : ''}`
    setDocumentTitle(newTitle)
}

function PageWrapperPrimary({ children }: PageWrapperPrimaryProps) {
    const { expressionMode, primary, underlying } = useSelector(selectPrimarySecurity)
    const primarySecurityKey = useSelector((state: RootState) => state.homeStore.primarySecurity.key)
    const primaryUnderlyingKey = useSelector((state: RootState) => state.homeStore.primarySecurity.underlyingSecurityCode)
    const recentExpressionsRefreshedTime = useSelector(selectRecentExpressionsRefreshTime)
    const dispatch = useDispatch()
    const location = useLocation()
    const recentExpressions = useSelector(selectRecentExpressions)
    const mainSecurity = expressionMode ? null : (underlying || primary)
    const snapshotSubscriptionToken = primaryUnderlyingKey || primarySecurityKey
    const prevToken = useRef<string>('')

    useEffect(() => {
        updateDocumentTitleFromStore()
        return store.subscribe(updateDocumentTitleFromStore)
    }, [])

    useEffect(() => {
        if (Math.abs(new Date().getTime() - recentExpressionsRefreshedTime) > (24 * 60 * 60 * 1000)) {
            recentExpressions.map((expression: RecentExpression) => ({
                id: expression.expression,
                name: '',
                expression: expression.expression,
                securities: expression.securities,
                expired: false
            }))
        }
    }, [recentExpressionsRefreshedTime, recentExpressions])

    useEffect(() => {
        const requiredUrl = [ROUTES.optionChain, ROUTES.strategyBuilder, ROUTES.openInterest, ROUTES.openInterestChange, ROUTES.multiStrikeChart, ROUTES.symbol, ROUTES.spread].some(route => matchPath({ path: route }, location.pathname))
        if (!requiredUrl || !snapshotSubscriptionToken || prevToken.current === snapshotSubscriptionToken) {
            return
        }

        const titleSecurity = resolveTitleSecurity(store.getState())
        const cachedPrimary = primarySecurityKey ? getSecurityFromCache(primarySecurityKey) : undefined
        const cachedUnderlying = primaryUnderlyingKey ? getSecurityFromCache(primaryUnderlyingKey) : undefined
        const wsSecurity = titleSecurity?.security ?? cachedUnderlying ?? cachedPrimary
        if (!wsSecurity) {
            return
        }

        prevToken.current = snapshotSubscriptionToken
        const payload: Partial<WebSocketRequestMessage_Subscription> = {
            securitySnapshotSubscriptions: [{
                tag: 'primary-security',
                securities: [{
                    exchange: wsSecurity.exchange,
                    code: wsSecurity.code
                }]
            }]
        }
        websocketActions.setWebsocketSubscribeList(dispatch, payload)
    }, [mainSecurity, location.pathname, snapshotSubscriptionToken, primarySecurityKey, primaryUnderlyingKey, dispatch])

    return children
}

export default memo(PageWrapperPrimary)
